// src/pages/admin/AdminAuditoria.js
//
// D-27 — Auditoría de la plataforma.
//
// Una fila por cada mutación que hizo alguien con privilegios y por cada
// mutación sobre una ruta sensible (contraseñas, roles, plata, permisos,
// bajas de cuenta). Responde las tres preguntas que antes no tenían
// respuesta: quién le cambió la contraseña a este tendero, quién concedió
// este SUPERADMIN, quién suspendió este negocio y cuándo.
//
// La tabla no se puede editar ni borrar: la API es de solo lectura a
// propósito. Si algo quedó mal registrado se registra la corrección.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container, Card, CardContent, Typography, Box, Stack, TextField, Button, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, IconButton, Tooltip, Alert,
  LinearProgress, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControlLabel, Switch, TablePagination,
} from '@mui/material';
import HistoryIcon     from '@mui/icons-material/History';
import RefreshIcon     from '@mui/icons-material/Refresh';
import DownloadIcon    from '@mui/icons-material/Download';
import VisibilityIcon  from '@mui/icons-material/Visibility';
import { admin } from '../../api/authApi';
import VistaCambioAuditoria, { parsearJson } from '../../components/admin/VistaCambioAuditoria';

const fechaCo = (iso, conHora = true) => {
  if (!iso) return '—';
  const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString('es-CO', {
    timeZone: 'America/Bogota', day: '2-digit', month: 'short', year: 'numeric',
    ...(conHora ? { hour: '2-digit', minute: '2-digit', second: '2-digit' } : {}),
  });
};

const chipResultado = (r, status) => {
  switch (r) {
    case 'OK':             return <Chip size="small" color="success" label="OK" />;
    case 'ERROR_CLIENTE':  return <Chip size="small" color="warning" label={`Rechazado${status ? ` ${status}` : ''}`} />;
    case 'ERROR_SERVIDOR': return <Chip size="small" color="error"   label={`Error ${status || 500}`} />;
    case 'EXCEPCION':      return <Chip size="small" color="error"   label="Falló" />;
    default:               return <Chip size="small" variant="outlined" label={r} />;
  }
};

// Las acciones que cambian quién puede hacer qué se marcan: son las que un
// auditor busca primero y las que no deberían pasar desapercibidas.
const ACCIONES_CRITICAS = new Set([
  'CAMBIAR_PASSWORD', 'ASIGNAR_ROL', 'QUITAR_ROL', 'ELIMINAR',
  'GUARDAR_SECRETO', 'ELIMINAR_SECRETO', 'ACTIVAR_SIN_PAGO',
  'CREAR_SUPER_ADMIN', 'INICIALIZAR_ROLES',
]);

const FILTROS_VACIOS = {
  actor: '', modulo: '', accion: '', entidad: '', entidadId: '',
  resultado: '', soloFallos: false, texto: '', desde: '', hasta: '',
};

export default function AdminAuditoria() {
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);
  const [aplicados, setAplicados] = useState(FILTROS_VACIOS);

  const [data, setData]     = useState({ total: 0, items: [] });
  const [page, setPage]     = useState(0);     // TablePagination es base 0
  const [size, setSize]     = useState(50);
  const [cargando, setCarga] = useState(false);
  const [error, setError]   = useState('');

  const [facetas, setFacetas] = useState(null);
  const [detalle, setDetalle] = useState(null);

  // ── Carga ────────────────────────────────────────────────────

  const cargar = useCallback(async () => {
    setCarga(true); setError('');
    try {
      const query = { page: page + 1, size };
      Object.entries(aplicados).forEach(([k, v]) => {
        if (v !== '' && v !== false && v != null) query[k] = v;
      });
      const r = await admin.auditoriaListar(query);
      setData({ total: r?.total || 0, items: r?.items || [] });
    } catch (e) {
      // El error se queda en pantalla con un botón para reintentar: una
      // tabla vacía sin explicación hace pensar que no hay registros.
      setError(e.mensajeUsuario || 'No se pudo cargar la auditoría.');
      setData({ total: 0, items: [] });
    } finally {
      setCarga(false);
    }
  }, [aplicados, page, size]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const f = await admin.auditoriaFacetas(180);
        if (vivo) setFacetas(f);
      } catch { /* los filtros quedan como texto libre; no es bloqueante */ }
    })();
    return () => { vivo = false; };
  }, []);

  const aplicar = () => { setPage(0); setAplicados(filtros); };
  const limpiar = () => { setPage(0); setFiltros(FILTROS_VACIOS); setAplicados(FILTROS_VACIOS); };

  const set = (campo) => (e) =>
    setFiltros((f) => ({ ...f, [campo]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  // ── Exportar ─────────────────────────────────────────────────

  const exportarCsv = () => {
    const cab = ['Fecha (Colombia)', 'Actor', 'Roles', 'Modulo', 'Accion',
                 'Entidad', 'EntidadId', 'Descripcion', 'Resultado', 'Status', 'IP', 'Ruta'];
    const filas = data.items.map((x) => [
      fechaCo(x.fecha), x.actor || 'anónimo', x.actorRoles || '', x.modulo, x.accion,
      x.entidad || '', x.entidadId || '', x.descripcion || '',
      x.resultado, x.statusCode ?? '', x.ip || '', x.ruta,
    ]);
    const escapar = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [cab, ...filas].map((f) => f.map(escapar).join(';')).join('\n');
    // El BOM es para que Excel en español abra las tildes bien.
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `auditoria_holaveci_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const resumen = useMemo(() => {
    if (!facetas) return null;
    return { total: facetas.total, fallos: facetas.fallos, dias: facetas.dias };
  }, [facetas]);

  // ── Render ───────────────────────────────────────────────────

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <HistoryIcon />
        <Typography variant="h5" fontWeight={700}>Auditoría</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Recargar">
          <IconButton onClick={cargar} disabled={cargando}><RefreshIcon /></IconButton>
        </Tooltip>
        <Button
          size="small" startIcon={<DownloadIcon />}
          onClick={exportarCsv} disabled={!data.items.length}
        >
          Exportar página
        </Button>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Quedan registradas todas las acciones de quien tiene privilegios y las
        acciones sensibles de cualquier usuario: contraseñas, roles, estados de
        negocio, secretos, cobros y bajas de cuenta. Esta tabla no se puede
        editar ni borrar.
      </Typography>

      {resumen && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}>
            <Card><CardContent>
              <Typography variant="caption" color="text.secondary">
                Acciones ({resumen.dias} días)
              </Typography>
              <Typography variant="h6">{resumen.total.toLocaleString('es-CO')}</Typography>
            </CardContent></Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card><CardContent>
              <Typography variant="caption" color="text.secondary">Fallaron o se rechazaron</Typography>
              <Typography variant="h6" color={resumen.fallos ? 'error.main' : 'text.primary'}>
                {resumen.fallos.toLocaleString('es-CO')}
              </Typography>
            </CardContent></Card>
          </Grid>
        </Grid>
      )}

      {/* ── Filtros ── */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Actor" fullWidth size="small"
                value={filtros.actor} onChange={set('actor')}
                placeholder="correo o nombre"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select label="Módulo" fullWidth size="small"
                value={filtros.modulo} onChange={set('modulo')}
              >
                <MenuItem value="">Todos</MenuItem>
                {(facetas?.modulos || []).map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select label="Acción" fullWidth size="small"
                value={filtros.accion} onChange={set('accion')}
              >
                <MenuItem value="">Todas</MenuItem>
                {(facetas?.acciones || []).map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select label="Entidad" fullWidth size="small"
                value={filtros.entidad} onChange={set('entidad')}
              >
                <MenuItem value="">Todas</MenuItem>
                {(facetas?.entidades || []).map((e) => <MenuItem key={e} value={e}>{e}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Id de la entidad" fullWidth size="small"
                value={filtros.entidadId} onChange={set('entidadId')}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label="Buscar" fullWidth size="small"
                value={filtros.texto} onChange={set('texto')}
                placeholder="descripción o ruta"
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Desde" type="date" fullWidth size="small"
                value={filtros.desde} onChange={set('desde')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                label="Hasta" type="date" fullWidth size="small"
                value={filtros.hasta} onChange={set('hasta')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                select label="Resultado" fullWidth size="small"
                value={filtros.resultado} onChange={set('resultado')}
                disabled={filtros.soloFallos}
              >
                <MenuItem value="">Todos</MenuItem>
                {(facetas?.resultados || ['OK', 'ERROR_CLIENTE', 'ERROR_SERVIDOR', 'EXCEPCION'])
                  .map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControlLabel
                control={<Switch checked={filtros.soloFallos} onChange={set('soloFallos')} />}
                label="Solo lo que falló"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={aplicar} disabled={cargando}>Aplicar</Button>
                <Button onClick={limpiar} disabled={cargando}>Limpiar</Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Tabla ── */}
      <Card>
        {cargando && <LinearProgress />}
        <CardContent>
          {error && (
            <Alert
              severity="error" sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={cargar}>Reintentar</Button>}
            >
              {error}
            </Alert>
          )}

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Módulo</TableCell>
                  <TableCell>Acción</TableCell>
                  <TableCell>Sobre</TableCell>
                  <TableCell>Qué pasó</TableCell>
                  <TableCell>Resultado</TableCell>
                  <TableCell align="right">Detalle</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!cargando && !error && data.items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No hay acciones registradas con esos filtros.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {data.items.map((x) => (
                  <TableRow key={x.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fechaCo(x.fecha)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{x.actor || 'anónimo'}</Typography>
                      {x.actorRoles && (
                        <Typography variant="caption" color="text.secondary">{x.actorRoles}</Typography>
                      )}
                    </TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={x.modulo} /></TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={ACCIONES_CRITICAS.has(x.accion) ? 'warning' : 'default'}
                        variant={ACCIONES_CRITICAS.has(x.accion) ? 'filled' : 'outlined'}
                        label={x.accion}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {x.entidad ? `${x.entidad} ${x.entidadId || ''}`.trim() : '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 380 }}>
                      <Typography variant="body2">{x.descripcion || <em>{x.metodo} {x.ruta}</em>}</Typography>
                      {x.mensajeError && (
                        <Typography variant="caption" color="error.main">{x.mensajeError}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{chipResultado(x.resultado, x.statusCode)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver el registro completo">
                        <IconButton size="small" onClick={() => setDetalle({ id: x.id })}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={data.total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 200]}
            labelRowsPerPage="Filas"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          />
        </CardContent>
      </Card>

      <DialogDetalleAuditoria
        id={detalle?.id}
        onCerrar={() => setDetalle(null)}
      />
    </Container>
  );
}

// ═══════════════════════════════════════════════════════════════
// Detalle
// ═══════════════════════════════════════════════════════════════

function DialogDetalleAuditoria({ id, onCerrar }) {
  const [fila, setFila]      = useState(null);
  const [cargando, setCarga] = useState(false);
  const [error, setError]    = useState('');

  useEffect(() => {
    if (id == null) { setFila(null); return; }
    let vivo = true;
    (async () => {
      setCarga(true); setError('');
      try {
        const r = await admin.auditoriaDetalle(id);
        if (vivo) setFila(r);
      } catch (e) {
        if (vivo) setError(e.mensajeUsuario || 'No se pudo cargar el registro.');
      } finally {
        if (vivo) setCarga(false);
      }
    })();
    return () => { vivo = false; };
  }, [id]);

  const Dato = ({ label, children }) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{children || '—'}</Typography>
    </Grid>
  );

  const cuerpo = fila ? parsearJson(fila.cuerpo) : null;
  const params = fila ? parsearJson(fila.parametros) : null;

  return (
    <Dialog open={id != null} onClose={onCerrar} maxWidth="md" fullWidth>
      <DialogTitle>Registro de auditoría #{id}</DialogTitle>
      <DialogContent dividers>
        {cargando && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error">{error}</Alert>}

        {fila && (
          <Stack spacing={2}>
            <Grid container spacing={1.5}>
              <Dato label="Cuándo">{fechaCo(fila.fecha)}</Dato>
              <Dato label="Quién">
                {fila.actor || 'anónimo'}
                {fila.actorRoles ? ` · ${fila.actorRoles}` : ''}
              </Dato>
              <Dato label="Qué">{`${fila.modulo} / ${fila.accion}`}</Dato>
              <Dato label="Sobre">
                {fila.entidad ? `${fila.entidad} ${fila.entidadId || ''}`.trim() : '—'}
              </Dato>
              <Dato label="Petición">{`${fila.metodo} ${fila.ruta}`}</Dato>
              <Dato label="Resultado">
                {`${fila.resultado}${fila.statusCode ? ` (${fila.statusCode})` : ''}`}
              </Dato>
              <Dato label="Desde dónde">{fila.ip}</Dato>
              <Dato label="Duró">{fila.duracionMs != null ? `${fila.duracionMs} ms` : '—'}</Dato>
              {fila.mensajeError && (
                <Grid item xs={12}>
                  <Alert severity="warning">{fila.mensajeError}</Alert>
                </Grid>
              )}
              {fila.userAgent && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Cliente</Typography>
                  <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all' }}>
                    {fila.userAgent}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Box>
              <Typography variant="subtitle2" gutterBottom>Qué cambió</Typography>
              <VistaCambioAuditoria antes={fila.antes} despues={fila.despues} />
            </Box>

            {(cuerpo || params) && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>Lo que se envió</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Las contraseñas, tokens y secretos se guardan como «redactado»:
                  la auditoría registra que se cambiaron, no su valor.
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0, p: 1, borderRadius: 1, bgcolor: 'action.hover', fontSize: 12,
                    overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify({ parametros: params, cuerpo }, null, 2)}
                </Box>
              </Box>
            )}

            {fila.traceId && (
              <Typography variant="caption" color="text.secondary">
                TraceId para cruzar con los logs del servidor: {fila.traceId}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
